import React, { useState, useEffect } from 'react';
import Lottie from 'react-lottie-player';
import { useParams } from 'react-router-dom';
import { useQuery, useLazyQuery, gql } from '@apollo/client';
import Button from 'Components/Button';
import Icon from 'Components/Icon/Icon';
import formatDate from 'Shared/util/formatDate';
import animationData from 'Shared/spiner_blue.json';
import { queryGetMobileBuilds } from '../TabMobileBuildsList/TabMobileBuildsList';
import AddMobileBuildForm from '../AddMobileBuild/AddMobileBuild.js';
import jsCookie from 'js-cookie';
import moment from 'moment';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { logger } from 'RootShared/utility/ErrorLogger';
import { toast } from 'react-toastify';

export const queryGetMobileBuildDetails = gql`
  query MobileBuildDetailQuery($publicHash: String!) @api(name: root) {
    build(publicHash: $publicHash) {
      id
      system
      buildType
      appFile
      changelog
      shareLink
      description
      installUrl
      absoluteUrl
      version
      buildVersion
      identifier
      title
      createdAt
      minOSVersion
      clientContract {
        id
        title
      }
    }
  }
`;

const HOST_NAME = process.env.HOST_NAME || '';
const ANDROID = 'ANDROID';

const MobileBuildsDetails = () => {
  const [isAddBuildModalOpen, setAddMobileBuildOpenStatus] = useState(false);
  const { publicHash } = useParams();
  const isAuthenticated = jsCookie.get('isAuthenticated');
  const { data: mobileBuild, loading } = useQuery(queryGetMobileBuildDetails, {
    variables: {
      publicHash: publicHash,
    },
    options: { cache: 'cache-first' },
  });

  useEffect(() => {
    const appItem = document.querySelector('.app').classList;
    if (!isAuthenticated) {
      appItem.add('app__without-navbar');
    } else {
      appItem.remove('app__without-navbar');
    }
  }, [isAuthenticated]);

  const [getPreviousBuilds, { data: previousBuilds, error: previousBuildError }] = useLazyQuery(queryGetMobileBuilds, {
    variables: {
      offset: 0,
      limit: 20,
      clientContractId: mobileBuild && +mobileBuild.build.clientContract.id,
    },
  });
  if (previousBuildError) {
    logger.error(previousBuildError);
    isAuthenticated &&
      toast('Something went wrong', {
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        closeButton: true,
        hideProgressBar: false,
      });
  }

  useEffect(() => {
    if (mobileBuild) {
      getPreviousBuilds();
    }
  }, [getPreviousBuilds, mobileBuild]);

  const handleOpenAddBuildModal = () => {
    setAddMobileBuildOpenStatus(!isAddBuildModalOpen);
  };

  const modal = isAddBuildModalOpen ? (
    <AddMobileBuildForm
      data={mobileBuild.build}
      projectName={mobileBuild.build.clientContract.title}
      handleOpenAddBuildModal={handleOpenAddBuildModal}
    />
  ) : null;

  const getUrlForInstallBuild = (build) => (build.system === ANDROID ? HOST_NAME + build.installUrl : build.installUrl);

  return (
    <>
      {loading && (
        <Lottie
          data-testid="tab_expenses_history"
          className="lottie"
          loop={true}
          play={true}
          animationData={animationData}
          width={100}
          style={{ width: 100, height: 100, margin: '0px auto' }}
        />
      )}
      {mobileBuild && (
        <div className="mobile-build__details" data-testid="mobile-builds-details">
          <div className="mobile-build">
            <div className="mobile-build__header">
              <div className="header__title" data-testid="mobile-build-header">
                <h1>{mobileBuild.build.clientContract.title}</h1>
                <p>{mobileBuild.build.createdAt && formatDate(mobileBuild.build.createdAt)}</p>
              </div>
              <div className="header__navigation">
                <div className="tag tag_variant_gray tag_size_small icon-copy__tag">Copied</div>
                <div className="navigation__icons">
                  <Icon
                    name="icon-copy"
                    onClick={(e) => {
                      navigator.clipboard.writeText(
                        `https://${window.location.host}/employee/builds/${mobileBuild.build.shareLink}/`
                      );
                      let emailTagClassList = e.target.offsetParent.getElementsByClassName('icon-copy__tag')[0]
                        .classList;
                      emailTagClassList.add('icon-copy__tag-active');
                      setTimeout(() => {
                        emailTagClassList.remove('icon-copy__tag-active');
                      }, 2000);
                    }}
                  />
                </div>
                <div data-testid="install-build">
                  <a href={getUrlForInstallBuild(mobileBuild.build)} className="button button_primary button_no-icon">
                    Install build
                  </a>
                </div>
              </div>
            </div>
            <div className="mobile-build__data">
              <div className="data__item">
                <h3>Version</h3>
                <p>{mobileBuild.build.version}</p>
              </div>
              <div className="data__item">
                <h3>Build version</h3>
                <p>{mobileBuild.build_version}</p>
              </div>
              <div className="data__item">
                <h3>Changelog</h3>
              </div>
              <div className="data__item">
                <h3>Type</h3>
                <p>{mobileBuild.build.buildType}</p>
              </div>
              <div className="data__item">
                <h3>Identifier</h3>
                <p>{mobileBuild.build.identifier}</p>
              </div>
              <div className="data__item">
                <h3>Minimum OS Version</h3>
                <p>{mobileBuild.build.minOSVersion}</p>
              </div>
              <div className="data__item">
                <h3>Description</h3>
                <p>{mobileBuild.build.description}</p>
              </div>
            </div>
          </div>
          {isAuthenticated && previousBuilds && (
            <div className="previous-version">
              <div className="previous-version__header">
                <h1>Previous version</h1>
              </div>
              <div className="previous-version__content">
                {previousBuilds.builds.map((item) => (
                  <div key={item.shareLink} className="previous-version__content-item">
                    <a href={`/employee/builds/${item.shareLink}/`} className="content-item__link">
                      <div className="content-item__date">
                        <span>{formatDate(item.createdAt)}</span>
                        <p>{moment(item.createdAt).local().format('HH:mm')}</p>
                      </div>
                      <Icon className="row-icon" name="icon-right" dataTestId={'icon-right-mobile-details'} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {isAuthenticated && (
        <Button
          icon="icon-plus"
          variant="primary"
          className="details-button__add-build"
          text="Add build"
          onClick={handleOpenAddBuildModal}
        />
      )}
      {modal}
    </>
  );
};

export default withErrorBoundary(MobileBuildsDetails);
